import { useState, useEffect, useCallback } from 'react';
import { fetchProjectsWithFilters } from '../api/projectsApi';
import type { ProjectWithCustomer, ProjectStatus } from '../types';

interface UseProjectsOptions {
  businessId: string | null;
  autoFetch?: boolean;
}

interface ProjectFilters {
  status?: ProjectStatus;
  customerId?: string;
  search?: string;
}

interface ProjectSorting {
  field: 'created_at' | 'start_date' | 'customer_name';
  direction: 'asc' | 'desc';
}

interface UseProjectsReturn {
  projects: ProjectWithCustomer[];
  isLoading: boolean;
  error: string | null;
  filters: ProjectFilters;
  sorting: ProjectSorting;
  setFilters: (filters: ProjectFilters) => void;
  setSorting: (sorting: ProjectSorting) => void;
  refetch: () => Promise<void>;
  clearFilters: () => void;
}

const DEFAULT_SORTING: ProjectSorting = {
  field: 'created_at',
  direction: 'desc',
};

/**
 * Hook to fetch and filter projects for a business
 */
export function useProjects({
  businessId,
  autoFetch = true,
}: UseProjectsOptions): UseProjectsReturn {
  const [projects, setProjects] = useState<ProjectWithCustomer[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<ProjectFilters>({});
  const [sorting, setSorting] = useState<ProjectSorting>(DEFAULT_SORTING);

  const loadProjects = useCallback(async () => {
    if (!businessId) {
      setProjects([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await fetchProjectsWithFilters(businessId, filters, sorting);
      setProjects(data);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to load projects';
      setError(message);
      console.error('[useProjects] Error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [businessId, filters, sorting]);

  const clearFilters = useCallback(() => {
    setFilters({});
    setSorting(DEFAULT_SORTING);
  }, []);

  useEffect(() => {
    if (autoFetch && businessId) {
      loadProjects();
    }
  }, [autoFetch, businessId, loadProjects]);

  return {
    projects,
    isLoading,
    error,
    filters,
    sorting,
    setFilters,
    setSorting,
    refetch: loadProjects,
    clearFilters,
  };
}